import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { Grid, TextField, Box, Typography, MenuItem, FormControlLabel, Switch } from '@mui/material'
import { Controller } from 'react-hook-form'

export const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/
const urlPattern =
    /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/

const extractHostname = url => {
    const hostname = url.includes('//') ? url.split('/')[2] : url.split('/')[0]
    return hostname.split(':')[0].split('?')[0].toLowerCase().replace('www.', '')
}

const CompanyDetails = ({
    selectedCompany,
    isNewCompany,
    control,
    errors,
    getValues,
    watch,
    trigger,
    setValue,
    locationData,
}) => {
    const [logoUrl, setLogoUrl] = useState(selectedCompany && !isNewCompany ? selectedCompany?.logoUrl : '')

    const url = watch('url')
    const email = watch('adminEmailAddress')

    const handleLogoUrlChange = event => {
        setLogoUrl(event.target.value)
    }

    const validateEmailDomain = useCallback(() => {
        const email = getValues('adminEmailAddress')
        const url = getValues('url')
        if (!email || !emailPattern.test(email)) return true
        const emailDomain = email.split('@')[1]?.toLowerCase()
        const urlDomain = extractHostname(url).toLowerCase()
        return !url || emailDomain === urlDomain || 'Email domain does not match company URL domain'
    }, [getValues])

    useEffect(() => {
        if (email) {
            trigger('adminEmailAddress')
        }
    }, [url, trigger])

    const filteredStates = useMemo(() => {
        const selectedCountryName = watch('address.countryName')
        const selectedCountry = locationData.countries.find(c => c.name === selectedCountryName)
        return locationData.states.filter(state => state.country_id === selectedCountry?.id)
    }, [locationData.states, locationData.countries, watch('address.countryName')])

    const filteredCities = useMemo(() => {
        const selectedStateName = watch('address.provinceName')
        const selectedState = locationData.states.find(s => s.name === selectedStateName)
        return locationData.cities.filter(city => city.state_id === selectedState?.id)
    }, [locationData.cities, locationData.states, watch('address.provinceName')])

    const handleCountryChange = useCallback(
        (event, field) => {
            const selectedCountryName = event.target.value
            const selectedCountry = locationData.countries.find(country => country.name === selectedCountryName)
            field.onChange(selectedCountryName)

            // Only store the country name
            setValue('address.countryName', selectedCountry?.name || '')
            setValue('address.provinceName', '') // Reset state
            setValue('address.cityName', '') // Reset city
        },
        [locationData.countries, setValue]
    )

    const handleStateChange = useCallback(
        (event, field) => {
            const selectedStateName = event.target.value
            const selectedState = locationData.states.find(state => state.name === selectedStateName)
            field.onChange(selectedStateName)

            // Only store the state name
            setValue('address.provinceName', selectedState?.name || '')
            setValue('address.cityName', '') // Reset city when state changes
        },
        [locationData.states, setValue]
    )

    const handleCityChange = useCallback(
        (event, field) => {
            const selectedCityName = event.target.value
            const selectedCity = locationData.cities.find(city => city.name === selectedCityName)
            field.onChange(selectedCityName)

            // Only store the city name
            setValue('address.cityName', selectedCity?.name || '')
        },
        [locationData.cities, setValue]
    )

    return (
        <Grid container spacing={2}>
            <Grid item xs={6}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    padding="10px"
                    bgcolor="#f0f0f0"
                    borderRadius="8px"
                >
                    <Typography variant="h6">Basic Info</Typography>
                </Box>

                <Controller
                    name="name"
                    control={control}
                    rules={{ required: 'Company Name is required' }}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label="Company Name"
                            fullWidth
                            error={!!errors.name}
                            helperText={errors.name ? errors.name.message : ''}
                            required
                            style={{ marginTop: '10px' }}
                        />
                    )}
                />

                <Controller
                    name="url"
                    control={control}
                    rules={{
                        required: 'URL is required',
                        pattern: { value: urlPattern, message: 'Enter a valid URL' },
                    }}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label="URL"
                            fullWidth
                            error={!!errors.url}
                            helperText={errors.url ? errors.url.message : ''}
                            required
                            style={{ marginTop: '10px' }}
                        />
                    )}
                />

                <Box display="flex" alignItems="center" mt={2}>
                    <Controller
                        name="logoUrl"
                        control={control}
                        rules={{
                            pattern: { value: urlPattern, message: 'Enter a valid URL' },
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Logo URL"
                                fullWidth
                                onChange={e => {
                                    handleLogoUrlChange(e)
                                    field.onChange(e)
                                }}
                                error={!!errors.logoUrl}
                                helperText={errors.logoUrl ? errors.logoUrl.message : ''}
                                style={{ marginRight: '10px' }}
                            />
                        )}
                    />
                    {logoUrl && (
                        <img
                            src={logoUrl}
                            alt="Company Logo"
                            style={{ maxHeight: '50px', objectFit: 'contain', marginLeft: '10px' }}
                        />
                    )}
                </Box>

                <Controller
                    name="adminEmailAddress"
                    control={control}
                    rules={{
                        required: 'Administrator Email is required',
                        pattern: {
                            value: emailPattern,
                            message: 'Enter a valid email address',
                        },
                        validate: validateEmailDomain,
                    }}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label="Administrator Email"
                            fullWidth
                            error={!!errors.adminEmailAddress}
                            helperText={errors.adminEmailAddress ? errors.adminEmailAddress.message : ''}
                            required
                            style={{ marginTop: '10px' }}
                        />
                    )}
                />

                <Controller
                    name="isAuthority"
                    control={control}
                    render={({ field }) => (
                        <>
                            <FormControlLabel
                                control={
                                    <Switch
                                        {...field}
                                        checked={field.value}
                                        onChange={field.onChange}
                                        color="primary"
                                    />
                                }
                                label="Can Manage Other Companies"
                                labelPlacement="start"
                                sx={{ marginTop: '10px', marginLeft: '0' }}
                            />

                            {field.value && (
                                <Typography color="error" variant="body2" style={{ marginTop: '10px' }}>
                                    Note: All users that belong to this company can add/modify/delete all companies.
                                </Typography>
                            )}
                        </>
                    )}
                />
            </Grid>

            <Grid item xs={6}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    padding="10px"
                    bgcolor="#f0f0f0"
                    borderRadius="8px"
                >
                    <Typography variant="h6">Location</Typography>
                </Box>

                <Controller
                    name="address.line1"
                    control={control}
                    render={({ field }) => (
                        <TextField {...field} label="Address Line 1" fullWidth style={{ marginTop: '10px' }} />
                    )}
                />

                <Controller
                    name="address.line2"
                    control={control}
                    render={({ field }) => (
                        <TextField {...field} label="Address Line 2" fullWidth style={{ marginTop: '10px' }} />
                    )}
                />

                <Controller
                    name="address.line3"
                    control={control}
                    render={({ field }) => (
                        <TextField {...field} label="Address Line 3" fullWidth style={{ marginTop: '10px' }} />
                    )}
                />

                {/* Country Dropdown */}
                <Controller
                    name="address.countryName"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            select
                            {...field}
                            label="Country"
                            fullWidth
                            value={field.value || ''}
                            onChange={e => handleCountryChange(e, field)}
                            style={{ marginTop: '10px' }}
                        >
                            {locationData.countries.length > 0 && (
                                <MenuItem value="">
                                    <em>-- Select Country --</em>
                                </MenuItem>
                            )}
                            {locationData.countries.length === 0 ? (
                                <MenuItem value="" disabled>
                                    No data available
                                </MenuItem>
                            ) : (
                                locationData.countries.map(country => (
                                    <MenuItem key={country.id} value={country.name}>
                                        {country.name}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                    )}
                />

                {/* State/Province Dropdown */}
                <Controller
                    name="address.provinceName"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            select
                            {...field}
                            label="State/Province"
                            fullWidth
                            value={field.value || ''}
                            onChange={e => handleStateChange(e, field)}
                            disabled={!watch('address.countryName')}
                            style={{ marginTop: '10px' }}
                        >
                            {filteredStates?.length > 0 && (
                                <MenuItem value="">
                                    <em>-- Select State --</em>
                                </MenuItem>
                            )}
                            {filteredStates.length === 0 ? (
                                <MenuItem value="" disabled>
                                    No data available
                                </MenuItem>
                            ) : (
                                filteredStates.map(state => (
                                    <MenuItem key={state.id} value={state.name}>
                                        {state.name}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                    )}
                />

                {/* City Dropdown */}
                <Controller
                    name="address.cityName"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            select
                            {...field}
                            label="City"
                            fullWidth
                            value={field.value || ''}
                            onChange={e => handleCityChange(e, field)}
                            disabled={!watch('address.provinceName')}
                            style={{ marginTop: '10px' }}
                        >
                            {filteredCities?.length > 0 && (
                                <MenuItem value="">
                                    <em>-- Select City --</em>
                                </MenuItem>
                            )}

                            {filteredCities.length === 0 ? (
                                <MenuItem value="" disabled>
                                    No data available
                                </MenuItem>
                            ) : (
                                filteredCities.map(city => (
                                    <MenuItem key={city.id} value={city.name}>
                                        {city.name}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                    )}
                />

                <Controller
                    name="address.postalCode"
                    control={control}
                    render={({ field }) => (
                        <TextField {...field} label="Postal Code" fullWidth style={{ marginTop: '10px' }} />
                    )}
                />
            </Grid>
        </Grid>
    )
}

export default CompanyDetails
